import { useCallback, useEffect, useRef, useState } from 'react';
import { getSyncStatus, postSyncMetadata, postSyncTrigger, postSyncBackfill } from '../services';
import { ISyncStatusItem, ISyncAcceptedResponse } from '../types';
import { resolveErrorMessage } from './hookUtils';

export interface UseSyncStatusResult {
	data: ISyncStatusItem[] | null;
	loading: boolean;
	error: string | null;
	firing: string | null;
	refetch: () => void;
	syncMetadata: () => Promise<ISyncAcceptedResponse>;
	syncTrigger: () => Promise<ISyncAcceptedResponse>;
	syncBackfill: () => Promise<ISyncAcceptedResponse>;
}

const REFETCH_DELAY_MS = 5000;

export function useSyncStatus(): UseSyncStatusResult {
	const [data, setData] = useState<ISyncStatusItem[] | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [firing, setFiring] = useState<string | null>(null);
	const [fetchCount, setFetchCount] = useState(0);
	const refetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		setError(null);

		getSyncStatus()
			.then((items) => {
				if (!cancelled) {
					setData(items);
					setLoading(false);
				}
			})
			.catch((err) => {
				if (!cancelled) {
					setError(resolveErrorMessage(err));
					setLoading(false);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [fetchCount]);

	useEffect(() => {
		return () => {
			if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
		};
	}, []);

	const refetch = useCallback(() => setFetchCount((n) => n + 1), []);

	// Programa un único refetch diferido post-sync (fire-and-forget)
	const scheduleRefetch = useCallback(() => {
		if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
		refetchTimerRef.current = setTimeout(() => {
			setFetchCount((n) => n + 1);
		}, REFETCH_DELAY_MS);
	}, []);

	const syncMetadata = useCallback(async () => {
		setFiring('metadata');
		setError(null);
		try {
			const result = await postSyncMetadata();
			scheduleRefetch();
			return result;
		} catch (err) {
			setError(resolveErrorMessage(err));
			throw err;
		} finally {
			setFiring(null);
		}
	}, [scheduleRefetch]);

	const syncTrigger = useCallback(async () => {
		setFiring('trigger');
		setError(null);
		try {
			const result = await postSyncTrigger();
			scheduleRefetch();
			return result;
		} catch (err) {
			setError(resolveErrorMessage(err));
			throw err;
		} finally {
			setFiring(null);
		}
	}, [scheduleRefetch]);

	const syncBackfill = useCallback(async () => {
		setFiring('backfill');
		setError(null);
		try {
			const result = await postSyncBackfill();
			scheduleRefetch();
			return result;
		} catch (err) {
			setError(resolveErrorMessage(err));
			throw err;
		} finally {
			setFiring(null);
		}
	}, [scheduleRefetch]);

	return { data, loading, error, firing, refetch, syncMetadata, syncTrigger, syncBackfill };
}
